import { observable, runInAction } from 'mobx';
import { debounce } from 'throttle-debounce';
import { PriceList } from '../pricelist/PriceListInterfaces';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';
import { filterContentBySearchTerm, getIndexForCategoryWithMostSearchTermHits, getPriceListTabItemId } from '../util/PriceListUtil';
import GaDataStore from './GaDataStore';
import LanguageStore from './LanguageStore';
import RootStore from './RootStore';

export default class PriceListStore {
  @observable accessor priceListFetchStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor priceListToDisplay: PriceList = null;
  @observable accessor currentTab: string = '';
  @observable accessor searchTerm: string = '';
  public readonly debouncedPriceListSearch: debounce<(searchTerm: string, isPrivateCustomer: boolean, isPricesWithVatHidden: boolean) => void>;

  @observable accessor originalPriceList: PriceList = null;

  private languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.debouncedPriceListSearch = debounce(250, this.handleSearch);
  }

  public fetchPriceList = async (priceListId: number) => {
    runInAction(() => {
      this.priceListFetchStatus = FetchStatus.FETCHING;
    });
    try {
      const {
        languageStore: { currentLanguage },
      } = this;
      const response = await API.get<PriceList>(`/pricelist/${priceListId}/${currentLanguage}`);
      runInAction(() => {
        this.priceListToDisplay = response.data;
        this.originalPriceList = response.data;
        this.currentTab = getPriceListTabItemId(1);
        this.priceListFetchStatus = FetchStatus.FETCHED;
      });
    } catch (e) {
      runInAction(() => {
        this.priceListFetchStatus = FetchStatus.ERROR;
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      });
    }
  };

  public pushPriceListGaData = (eventAction: string, eventLabel: string) => {
    GaDataStore.pushWithoutCmsData({
      event: 'AutoEvent',
      eventCategory: `Price list - id=${this.priceListToDisplay.id}`,
      eventAction: eventAction,
      eventLabel: eventLabel,
    });
  };

  public handleCategoryChange = (tabName: string) => {
    if (!tabName) {
      return;
    }
    runInAction(() => {
      this.currentTab = tabName;
      this.pushPriceListGaData('Tab change', tabName);
    });
  };

  public clearSearch = () => {
    runInAction(() => {
      this.searchTerm = '';
      this.priceListToDisplay = Object.assign({}, this.originalPriceList);
    });
  };

  private handleSearch = (searchTerm: string, isPrivateCustomer: boolean, isPricesWithVatHidden: boolean) => {
    runInAction(() => {
      if (searchTerm.length < 2) {
        return this.clearSearch();
      }
      this.searchTerm = searchTerm;
      this.priceListToDisplay.categories = filterContentBySearchTerm(searchTerm, this.originalPriceList.categories, isPrivateCustomer, isPricesWithVatHidden);
      this.changeCurrentTabAfterSearch();
      this.pushPriceListGaData('Search', searchTerm);
    });
  };

  private changeCurrentTabAfterSearch = () => {
    runInAction(() => {
      const currentTabIndex = +this.currentTab.match(/\d+$/);
      if (!this.priceListToDisplay.categories.some((category) => category.containsSearchTerm)) {
        return;
      }
      if (this.priceListToDisplay.categories[currentTabIndex - 1].containsSearchTerm) {
        return;
      }
      this.currentTab = getPriceListTabItemId(getIndexForCategoryWithMostSearchTermHits(this.priceListToDisplay.categories) + 1);
    });
  };
}
